<template>
  <a-layout class="container">
    <a-layout-content class="content">
      <!-- 第一部分：组合统计图 -->
      <div class="chart-section">
        <a-card title="招生人数趋势分析" class="chart-card">
          <div class="chart-container">
            <v-chart :option="enrollmentChartOption" :autoresize="true" style="height: 400px" />
          </div>
        </a-card>
      </div>

      <!-- 第二部分：表格和折线图 -->
      <div class="bottom-section">
        <!-- 左边：学位余缺数表格 -->
        <div class="table-container">
          <a-card title="学位余缺情况预测" class="table-card">
            <a-table
              :columns="degreeColumns"
              :data="degreeData"
              :pagination="false"
              :scroll="{ x: 900, y: 280 }"
              bordered
              size="small"
            >
              <template #cell="{ column, record }">
                <span :class="getValueClass(record[column.dataIndex])">
                  {{ formatValue(record[column.dataIndex]) }}
                </span>
              </template>
            </a-table>
            <div class="table-note">
              <p>说明：预测数据为负说明学位存在缺口，预测数据为正说明学位出现富余。</p>
            </div>
          </a-card>
        </div>

        <!-- 右边：在校生人数折线图 -->
        <div class="chart-container-right">
          <a-card title="在校学生人数趋势（堆叠图）" class="chart-card">
            <div class="chart-container">
              <v-chart :option="studentChartOption" :autoresize="true" style="height: 350px" />
            </div>
          </a-card>
        </div>
      </div>
    </a-layout-content>
  </a-layout>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { Card, Layout, Table } from '@arco-design/web-vue';
import VChart from 'vue-echarts';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart, LineChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DatasetComponent,
  MarkLineComponent,
} from 'echarts/components';

// 注册ECharts组件
use([
  CanvasRenderer,
  BarChart,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DatasetComponent,
  MarkLineComponent,
]);

// 招生人数数据（来自招生人数表）
const enrollmentData = {
  years: [
    '2015',
    '2016',
    '2017',
    '2018',
    '2019',
    '2020',
    '2021',
    '2022',
    '2023',
    '2024',
    '2025',
    '2026',
    '2027',
    '2028',
    '2029',
    '2030',
    '2031',
    '2032',
    '2033',
    '2034',
    '2035',
  ],
  birth: [
    20043, 24716, 30536, 25829, 24013, 21470, 18457.904, 16691, 16281, 16799, 16463, 16134, 15811,
    15495, 15185, 14881, 15029, 15180, 15331, 15485, 15640,
  ],
  preschool: [
    34282, 31548, 31502, 30802, 26753, 34607, 28620, 25482, 23362, 18793, 18102, 18312, 18944,
    17329, 16983, 16643, 16310, 15984, 15664, 15820, 15979,
  ],
  primary: [
    23465, 23619, 23985, 24558, 24784, 24213, 22495, 23199, 30299, 26553, 24535, 21942, 18863,
    17058, 16639, 17168, 16825, 16488, 16158, 15835, 15519,
  ],
  junior: [
    29501, 27972, 26854, 24503, 23140, 22598, 22043, 22394, 22739, 23390, 23121, 24213, 22495,
    23199, 30299, 26553, 24535, 21942, 18863, 17058, 16639,
  ],
  senior: [
    13055, 13111, 13475, 13625, 13540, 15130, 14135, 13947, 12605, 13352, 14010, 14735, 14797,
    15738, 14846, 15543, 18321, 20603, 17174, 15359, 14204,
  ],
};

// 在校学生人数数据（来自在校学生人数表）
const studentData = {
  kindergarten: [
    63733, 63281, 65106, 63603, 64021, 71959, 77429, 79412, 71032, 62393, 52539, 48136, 48268,
    47495, 46166, 43864, 42845, 41846, 40868, 40378, 40373,
  ],
  primarySchool: [
    154082, 148184, 143946, 142540, 142679, 142944, 142095, 141907, 148043, 149857, 155609, 153338,
    149707, 143566, 129907, 120522, 112812, 107359, 104654, 103431, 102311,
  ],
  juniorMiddle: [
    89489, 85639, 82621, 78066, 73836, 69837, 67488, 66759, 66762, 67975, 70614, 72088, 71193,
    71271, 77357, 81415, 82751, 74394, 66705, 59228, 53925,
  ],
  seniorHigh: [
    38397, 38248, 38513, 38942, 39277, 40761, 40436, 40215, 39740, 39256, 41517, 43648, 45094,
    46822, 46933, 47679, 52544, 56019, 57650, 52406, 47289,
  ],
};

// 学位余缺数据（来自学位余缺数表）
const degreeColumns = [
  { title: '学段', dataIndex: 'segment', width: 110, fixed: 'left' },
  { title: '2025', dataIndex: '2025', width: 65 },
  { title: '2026', dataIndex: '2026', width: 65 },
  { title: '2027', dataIndex: '2027', width: 65 },
  { title: '2028', dataIndex: '2028', width: 65 },
  { title: '2029', dataIndex: '2029', width: 65 },
  { title: '2030', dataIndex: '2030', width: 65 },
  { title: '2031', dataIndex: '2031', width: 65 },
  { title: '2032', dataIndex: '2032', width: 65 },
  { title: '2033', dataIndex: '2033', width: 65 },
  { title: '2034', dataIndex: '2034', width: 65 },
  { title: '2035', dataIndex: '2035', width: 65 },
];

const degreeData = ref([
  {
    key: '1',
    segment: '幼儿园学位缺口/富余',
    2025: 9854,
    2026: 4402,
    2027: -132,
    2028: 773,
    2029: 1330,
    2030: 2301,
    2031: 1019,
    2032: 999,
    2033: 979,
    2034: 490,
    2035: 5,
  },
  {
    key: '2',
    segment: '小学学位缺口/富余',
    2025: -5752,
    2026: 2271,
    2027: 3632,
    2028: 6141,
    2029: 13660,
    2030: 9384,
    2031: 7710,
    2032: 5453,
    2033: 2705,
    2034: 1222,
    2035: 1120,
  },
  {
    key: '3',
    segment: '初中学位缺口/富余',
    2025: -2639,
    2026: -1474,
    2027: 895,
    2028: -78,
    2029: -6086,
    2030: -4058,
    2031: -1336,
    2032: 8357,
    2033: 7689,
    2034: 7477,
    2035: 5303,
  },
  {
    key: '4',
    segment: '高中学位缺口/富余',
    2025: -2262,
    2026: -2131,
    2027: -1445,
    2028: -1728,
    2029: -111,
    2030: -746,
    2031: -4865,
    2032: -3475,
    2033: -1631,
    2034: 5244,
    2035: 5117,
  },
]);

// 格式化数值
const formatValue = (value) => {
  if (value === '' || value === null || value === undefined) return '-';
  return Number(value).toLocaleString('zh-CN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

// 根据数值正负设置样式
const getValueClass = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const num = Number(value);
  if (num > 0) return 'positive-value';
  if (num < 0) return 'negative-value';
  return '';
};

// 简化数值显示格式，用于图表标签
const formatChartValue = (value) => {
  if (value >= 10000) {
    return (value / 10000).toFixed(1) + '万';
  }
  return value.toLocaleString('zh-CN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

// 招生人数组合图表配置
const enrollmentChartOption = computed(() => ({
  title: {
    text: '玉溪市2015-2035年出生人数与各类型学校招生人数',
    left: 'center',
    textStyle: { fontSize: 26, fontWeight: 'bold' },
    top: 0,
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'cross',
      crossStyle: {
        color: '#999',
      },
    },
    formatter: function (params) {
      let result = `${params[0].axisValue}<br/>`;
      params.forEach((item) => {
        const value = formatChartValue(item.value);
        const color = item.color;
        result += `<span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background-color:${color}"></span>`;
        result += `${item.seriesName}: ${value}<br/>`;
      });
      return result;
    },
  },
  legend: {
    data: ['出生人口', '学前入园数', '小学招生数', '初中招生数', '高中招生数'],
    top: 30,
    textStyle: {
      fontSize: 11,
    },
  },
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    containLabel: true,
  },
  xAxis: [
    {
      type: 'category',
      data: enrollmentData.years,
      axisPointer: {
        type: 'shadow',
      },
      axisLabel: {
        interval: 0,
        rotate: 45,
        fontSize: 11,
      },
    },
  ],
  yAxis: [
    {
      type: 'value',
      name: '人数',
      min: 0,
      axisLabel: {
        formatter: function (value) {
          if (value >= 10000) {
            return value / 10000 + '万';
          }
          return value;
        },
        fontSize: 11,
      },
    },
    {
      type: 'value',
      name: '人数',
      min: 0,
      position: 'right',
      axisLabel: {
        formatter: function (value) {
          if (value >= 10000) {
            return value / 10000 + '万';
          }
          return value;
        },
        fontSize: 11,
      },
    },
  ],
  series: [
    {
      name: '出生人口',
      type: 'bar',
      data: enrollmentData.birth,
      itemStyle: {
        color: '#5470c6',
      },
      label: {
        show: true,
        position: 'top',
        formatter: function (params) {
          return formatChartValue(params.value);
        },
        fontSize: 10,
        color: '#5470c6',
      },
    },
    {
      name: '学前入园数',
      type: 'line',
      yAxisIndex: 1,
      data: enrollmentData.preschool,
      itemStyle: {
        color: '#91cc75',
      },
      label: {
        show: true,
        position: 'top',
        formatter: function (params) {
          return formatChartValue(params.value);
        },
        fontSize: 10,
        color: '#91cc75',
      },
      smooth: true,
    },
    {
      name: '小学招生数',
      type: 'line',
      yAxisIndex: 1,
      data: enrollmentData.primary,
      itemStyle: {
        color: '#fac858',
      },
      label: {
        show: true,
        position: 'top',
        formatter: function (params) {
          return formatChartValue(params.value);
        },
        fontSize: 10,
        color: '#fac858',
      },
      smooth: true,
    },
    {
      name: '初中招生数',
      type: 'line',
      yAxisIndex: 1,
      data: enrollmentData.junior,
      itemStyle: {
        color: '#ee6666',
      },
      label: {
        show: true,
        position: 'top',
        formatter: function (params) {
          return formatChartValue(params.value);
        },
        fontSize: 10,
        color: '#ee6666',
      },
      smooth: true,
    },
    {
      name: '高中招生数',
      type: 'line',
      yAxisIndex: 1,
      data: enrollmentData.senior,
      itemStyle: {
        color: '#73c0de',
      },
      label: {
        show: true,
        position: 'top',
        formatter: function (params) {
          return formatChartValue(params.value);
        },
        fontSize: 10,
        color: '#73c0de',
      },
      smooth: true,
    },
  ],
}));

// 在校生人数折线图配置
const studentChartOption = computed(() => ({
  title: {
    text: '玉溪市2025-2035年各学段在校学生人数',
    left: 'center',
    textStyle: { fontSize: 16, fontWeight: 'bold' },
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'cross',
      label: {
        backgroundColor: '#6a7985',
      },
    },
    formatter: function (params) {
      let result = `${params[0].axisValue}<br/>`;
      params.forEach((item) => {
        const value = formatChartValue(item.value);
        const color = item.color;
        result += `<span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background-color:${color}"></span>`;
        result += `${item.seriesName}: ${value}<br/>`;
      });
      return result;
    },
  },
  legend: {
    data: ['幼儿园在园数', '小学在校生数', '初中在校生数', '高中在校生数'],
    top: 20,
    textStyle: {
      fontSize: 11,
    },
  },
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    containLabel: true,
  },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: enrollmentData.years,
    axisLabel: {
      interval: 0,
      rotate: 45,
      fontSize: 11,
    },
  },
  yAxis: {
    type: 'value',
    axisLabel: {
      formatter: function (value) {
        if (value >= 10000) {
          return value / 10000 + '万';
        }
        return value;
      },
      fontSize: 11,
    },
  },
  series: [
    {
      name: '幼儿园在园数',
      type: 'line',
      stack: 'Total',
      data: studentData.kindergarten,
      itemStyle: {
        color: '#91cc75',
      },
      label: {
        show: true,
        position: 'top',
        formatter: function (params) {
          return formatChartValue(params.value);
        },
        fontSize: 10,
        color: '#91cc75',
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(145, 204, 117, 0.4)' },
            { offset: 1, color: 'rgba(145, 204, 117, 0.1)' },
          ],
        },
      },
      smooth: true,
    },
    {
      name: '小学在校生数',
      type: 'line',
      stack: 'Total',
      data: studentData.primarySchool,
      itemStyle: {
        color: '#fac858',
      },
      label: {
        show: true,
        position: 'top',
        formatter: function (params) {
          return formatChartValue(params.value);
        },
        fontSize: 10,
        color: '#fac858',
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(250, 200, 88, 0.4)' },
            { offset: 1, color: 'rgba(250, 200, 88, 0.1)' },
          ],
        },
      },
      smooth: true,
    },
    {
      name: '初中在校生数',
      type: 'line',
      stack: 'Total',
      data: studentData.juniorMiddle,
      itemStyle: {
        color: '#ee6666',
      },
      label: {
        show: true,
        position: 'top',
        formatter: function (params) {
          return formatChartValue(params.value);
        },
        fontSize: 10,
        color: '#ee6666',
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(238, 102, 102, 0.4)' },
            { offset: 1, color: 'rgba(238, 102, 102, 0.1)' },
          ],
        },
      },
      smooth: true,
    },
    {
      name: '高中在校生数',
      type: 'line',
      stack: 'Total',
      data: studentData.seniorHigh,
      itemStyle: {
        color: '#73c0de',
      },
      label: {
        show: true,
        position: 'top',
        formatter: function (params) {
          return formatChartValue(params.value);
        },
        fontSize: 10,
        color: '#73c0de',
      },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(115, 192, 222, 0.4)' },
            { offset: 1, color: 'rgba(115, 192, 222, 0.1)' },
          ],
        },
      },
      smooth: true,
    },
  ],
}));
</script>

<style scoped>
.container {
  height: 100vh;
  background-color: #f5f5f5;
}

.content {
  padding: 12px;
  overflow-y: auto;
}

.chart-section {
  margin-bottom: 16px;
}

.bottom-section {
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 16px;
  width: 100%;
}

.table-container {
  width: 100%;
}

.chart-container-right {
  width: 100%;
}

.chart-card,
.table-card {
  height: 100%;
  min-height: 450px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
}

.chart-container {
  height: 100%;
}

.table-note {
  margin-top: 8px;
  padding: 8px;
  background-color: #f9f9f9;
  border-radius: 4px;
  border-left: 4px solid #1890ff;
}

.table-note p {
  margin: 0;
  font-size: 16px;
  color: #666;
  line-height: 1.3;
}

.positive-value {
  color: #52c41a;
  font-weight: 500;
}

.negative-value {
  color: #f5222d;
  font-weight: 500;
}

:deep(.arco-card-body) {
  height: calc(100% - 56px);
  padding: 12px;
}

:deep(.arco-table) {
  font-size: 11px;
}

:deep(.arco-table-th) {
  font-size: 11px;
  padding: 6px 3px;
  font-weight: 600;
  line-height: 1.2;
}

:deep(.arco-table-td) {
  font-size: 11px;
  padding: 6px 3px;
  line-height: 1.2;
}

:deep(.arco-table-body) {
  max-height: 280px !important;
}

:deep(.arco-table-cell) {
  padding: 4px 3px;
}

:deep(.arco-card-header-title) {
  font-size: 13px;
  font-weight: 600;
}

:deep(.echarts) {
  font-size: 10px;
}

@media (max-width: 1200px) {
  .bottom-section {
    grid-template-columns: 1fr;
  }
}
</style>
